import { useEffect, useState, useRef } from 'react'
import { Bar } from '@nivo/bar'

import { colorService } from '../common/color.service'
import { xLabelOffset, yLabelOffset, tickSize } from '../../../reports/blocks/common/axes/constants'
import { Legend } from '../common/legend/legend'
import { Tooltip } from '../common/tooltip/tooltip'
import { TooltipContextProvider, useTooltipDisplayContext } from '../common/tooltip/tooltip.context'
import { formatService } from '../common/format.service'
import { barChartService } from './bar-chart.service'

import { nivoTheme } from './bar.style'

const FORMATS = {
  NUMBER: 'number',
  PERCENT: 'percent'
}

const DEFAULT_Y_VALUE_DOMAIN = ['auto', 'auto']
const DEFAULT_MARGIN_TOP = 50
const DEFAULT_MARGIN_RIGHT = 10
const DEFAULT_MARGIN_BOTTOM = 60
const DEFAULT_MARGIN_LEFT = 65

export function BarChartContainer (props = {}) {
  const containerRef = useRef()
  const [colors, setColors] = useState([])
  const nextColorIndex = useRef(0)
  const colorCache = useRef({})
  const usedColors = useRef([])

  const {
    children,
    data,
    datasets,
    xField = 'x',
    yField = 'n',
    groupField = 'group',
    labels,
    defaultLegendToggles
  } = props

  function getTransformedDatasets () {
    let dataList = datasets
    if (data && !dataList) {
      dataList = [data]
    }

    // Transform incoming datasets to pass transformed data to child components
    const transformedDatasets = dataList.map((data) => {
      const transformedData = transformData(data)
      const colors = transformedData.keys.map(getColor)
      return { ...transformedData, colors }
    })

    return transformedDatasets
  }

  const [chartDatasets, setChartDatasets] = useState(getTransformedDatasets())
  const [legendLabels, setLegendLabels] = useState([])
  const [legendToggles, setLegendToggles] = useState()

  useEffect(() => {
    setLegendToggles(defaultLegendToggles || {})
  }, [ defaultLegendToggles ])

  useEffect(() => {
    usedColors.current = []

    const transformedDatasets = getTransformedDatasets()

    // Get all colors from the transformedDatasets array
    const colors = transformedDatasets.reduce((colors, data) => {
      data.colors.forEach((color) => {
        if (!colors.includes(color)) {
          colors.push(color)
        }
      })
      return colors
    }, [])

    // Get legend labels based off transformed datasets
    const legendLabels = transformedDatasets.reduce((labels, data) => {
      data.labels.forEach((label) => {
        if (!labels.includes(label)) {
          labels.push(label)
        }
      })
      return labels
    }, [])

    // Update state data
    setChartDatasets(transformedDatasets)
    setColors(colors)
    setLegendLabels(legendLabels)
  }, [ data, datasets, xField, yField, groupField, labels ])

  function transformData (data) {
    const transformed = barChartService.transformData(data, { xField, yField, groupField, labels })
    return { ...transformed }
  }

  function getColor (key) {
    if (colorCache.current[key]) {
      return colorCache.current[key]
    } else {
      const { color, nextIndex } = colorService.pickNextBestColor({
        usedColors: usedColors.current,
        cachedColor: colorCache.current[key],
        nextIndex: nextColorIndex.current
      })
      colorCache.current[key] = color
      usedColors.current.push(color)
      nextColorIndex.current = nextIndex
      return color
    }
  }

  return (
    <div ref={containerRef}>
      {children({
        colors,
        legendToggles,
        legendLabels,
        onLegendToggle: setLegendToggles,
        chartDatasets,
        containerRef
      })}
    </div>
  )
}

export function BarChart (props = {}) {
  const ref = useRef()

  return (
    <div ref={ref}>
      <BarChartContainer {...props}>
        {({ colors, chartDatasets, legendToggles, legendLabels, onLegendToggle, containerRef }) => (
          <>
            <Legend labels={legendLabels || []} colors={colors} toggleDefaults={legendToggles} onToggle={onLegendToggle} />
            <SingleBarChart
              containerRef={containerRef}
              chartData={chartDatasets && chartDatasets.length ? chartDatasets[0] : {}}
              legendToggles={legendToggles}
              {...props}
            />
          </>
        )}
      </BarChartContainer>
    </div>
  )
}

export function SingleBarChart ({
  width = 1024,
  height,
  chartData,
  xField = 'x',
  axisLabels = { x: '', y: '' },
  hiddenAxisLabels = [],
  tickValues = {},
  yFormat = FORMATS.NUMBER,
  legendToggles,
  yValueDomain,
  groupMode,
  padding = 0.4,
  onClick = function () {},
  marginTop,
  marginRight,
  marginBottom,
  marginLeft
}) {
  const [bars, setBars] = useState(getVisibleBars(legendToggles))

  useEffect(() => {
    setBars(getVisibleBars(legendToggles))
  }, [ chartData, legendToggles ])

  function getVisibleBars (legendToggles = {}) {
    const visibleBars = { keys: [], labels: [], colors: [] }
    if (chartData && chartData.labels) {
      chartData.labels.forEach((label, i) => {
        if (!legendToggles[label]) {
          visibleBars.keys.push(chartData.keys[i])
          visibleBars.labels.push(chartData.labels[i])
          visibleBars.colors.push(chartData.colors[i])
        }
      })
    }
    if (visibleBars.labels.length === 1 && axisLabels && axisLabels.y) {
      visibleBars.labels[0] = axisLabels.y
    }
    return visibleBars
  }

  return (
    <TooltipContextProvider>
      <BarWithTooltip
        width={width}
        height={height}
        marginTop={marginTop}
        marginRight={marginRight}
        marginBottom={marginBottom}
        marginLeft={marginLeft}
        data={chartData.data || []}
        indexBy={xField}
        keys={bars.keys}
        labels={bars.labels}
        colors={bars.colors}
        groupMode={groupMode}
        axisLabels={axisLabels}
        hiddenAxisLabels={hiddenAxisLabels}
        tickValues={tickValues}
        yFormat={yFormat}
        yValueDomain={yValueDomain}
        padding={padding}
        onClick={onClick}
      />
    </TooltipContextProvider>
  )
}

function BarWithTooltip ({
  width,
  height,
  marginTop = DEFAULT_MARGIN_TOP,
  marginRight = DEFAULT_MARGIN_RIGHT,
  marginBottom = DEFAULT_MARGIN_BOTTOM,
  marginLeft = DEFAULT_MARGIN_LEFT,
  labels,
  axisLabels,
  hiddenAxisLabels = [],
  tickValues,
  yFormat,
  yValueDomain,
  ...props
}) {
  const [, setTooltipDisplayData] = useTooltipDisplayContext()

  function handleMouseEnter ({ id, indexValue, value }, event) {
    event.target.style.opacity = 0.8

    const primaryValue = formatService.formatValue(value, yFormat)

    setTooltipDisplayData({
      primaryLabel: getLabel(id),
      primaryValue,
      secondaryLabel: axisLabels.x,
      secondaryValue: indexValue
    })
  }

  function handleMouseLeave (data, event) {
    event.target.style.opacity = 1
    setTooltipDisplayData()
  }

  function getLabel (key) {
    if (labels) {
      const index = props.keys.indexOf(key)
      if (index !== -1) {
        return labels[index]
      }
    }
    return key
  }

  let minValue = DEFAULT_Y_VALUE_DOMAIN[0]
  let maxValue = DEFAULT_Y_VALUE_DOMAIN[1]
  if (yValueDomain && yValueDomain[0]) {
    minValue = yValueDomain[0]
  }
  if (yValueDomain && yValueDomain[1]) {
    maxValue = yValueDomain[1]
  }

  return <>
    <Bar
      { ...props }
      width={width}
      height={(height || width / 2) - (DEFAULT_MARGIN_TOP - marginTop)}
      margin={{ top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft !== undefined ? marginLeft : DEFAULT_MARGIN_LEFT }}
      enableGridY={false}
      enableLabel={false}
      axisBottom={{
        legend: axisLabels.x || '',
        tickSize,
        tickValues: tickValues.x ? tickValues.x.map(String) : undefined,
        legendPosition: 'middle',
        legendOffset: xLabelOffset
      }}
      axisLeft={{
        legend: (!hiddenAxisLabels.includes('y') && axisLabels.y) || '',
        tickValues: tickValues.y ? tickValues.y.map(String) : undefined,
        legendPosition: 'middle',
        legendOffset: -yLabelOffset,
        format: (value) => {
          return barChartService.formatAxisValue(value, yFormat)
        }
      }}
      minValue={minValue}
      maxValue={maxValue}
      isInteractive={true}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      theme={nivoTheme}
      tooltip={function () { return null }} // This is Nivo sanctioned, I promise!
    />
    <Tooltip />
  </>
}
